import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import {
  ChevronRight,
  Download,
  LifeBuoy,
  Mail,
  MessageSquare,
  Send,
  ShieldAlert,
  Ticket,
} from "lucide-solid";
import { Link } from "@/lib/u-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@/components/ui/text-field";
import { Skeleton } from "@/components/ui/skeleton";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@famidex/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { showToast } from "@/components/ui/toast";

type ErrorModel = components["schemas"]["ErrorModel"];
type SupportTicketListResponse = components["schemas"]["SupportTicketListResponse"];

const TEXTAREA_CLASS =
  "flex min-h-[96px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50";

const CATEGORIES = ["account", "bug", "purchase", "other"] as const;
type Category = (typeof CATEGORIES)[number];

// eslint-disable-next-line lingui/no-unlocalized-strings -- brand support email, not UI copy
const SUPPORT_EMAIL = "ayuda@famidex.com";

/**
 * "Soporte y Ayuda" — pantalla FamiDex de soporte para el usuario. Contacto,
 * alta de un ticket (POST /api/support/tickets), lista de sus tickets, y los
 * accesos de privacidad/cuenta (export + borrado, reusando la página de
 * delete-account existente).
 */
export function SupportHelp() {
  const { t } = useI18n();
  const session = useSession();

  const [subject, setSubject] = createSignal("");
  const [body, setBody] = createSignal("");
  const [category, setCategory] = createSignal<Category>("account");

  const myTickets = createApiQuery<SupportTicketListResponse, ErrorModel>(
    () => ["support", "tickets", "mine"],
    async () => api.GET("/api/support/tickets/mine"),
    () => ({ staleTime: 15_000, enabled: session.isAuthenticated() }),
  );

  const createMutation = createApiMutation(
    async (vars: { subject: string; body: string; category: string }) =>
      api.POST("/api/support/tickets", { body: vars }),
  );

  const submit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!subject().trim() || !body().trim()) {
      return;
    }
    try {
      await createMutation.mutateAsync({
        subject: subject().trim(),
        body: body().trim(),
        category: category(),
      });
      showToast(t(msg`Ticket sent. We'll get back to you soon.`), "success");
      setSubject("");
      setBody("");
      setCategory("account");
      await myTickets.refetch();
    } catch {
      showToast(t(msg`Couldn't send your ticket. Please try again.`), "error");
    }
  };

  const categoryLabel = (c: Category) =>
    c === "account"
      ? t(msg`Account`)
      : c === "bug"
        ? t(msg`Bug`)
        : c === "purchase"
          ? t(msg`Purchase`)
          : t(msg`Other`);

  return (
    <div class="space-y-8">
      <header class="flex items-center gap-3">
        <span class="grid h-11 w-11 place-items-center rounded-xl bg-accent/15 text-accent ring-1 ring-accent/20">
          <LifeBuoy class="h-6 w-6" />
        </span>
        <div>
          <h1 class="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            {t(msg`Support & Help`)}
          </h1>
          <p class="text-sm text-muted-foreground">{t(msg`How can we help you bloom today?`)}</p>
        </div>
      </header>

      {/* Contact */}
      <Card>
        <CardContent class="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex items-start gap-4">
            <span class="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent/10 text-accent">
              <Mail class="h-5 w-5" />
            </span>
            <div>
              <p class="font-semibold text-foreground">{t(msg`Email us`)}</p>
              <p class="text-sm text-muted-foreground">{t(msg`We reply within 24 hours.`)}</p>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                class="text-sm font-medium text-accent underline-offset-4 hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
          <MessageSquare class="hidden h-8 w-8 text-muted-foreground/40 sm:block" />
        </CardContent>
      </Card>

      {/* New ticket */}
      <Show
        when={session.isAuthenticated()}
        fallback={
          <Card>
            <CardContent class="p-6 text-sm text-muted-foreground">
              {t(msg`Sign in to open a support ticket and track our replies.`)}
            </CardContent>
          </Card>
        }
      >
        <Card>
          <CardContent class="p-6">
            <h2 class="mb-4 flex items-center gap-2 text-lg font-semibold text-foreground">
              <Ticket class="h-5 w-5 text-accent" />
              {t(msg`Open a ticket`)}
            </h2>
            <form onSubmit={submit} class="space-y-4">
              <div class="space-y-2">
                <Label for="ticket-subject">{t(msg`Subject`)}</Label>
                <TextField
                  id="ticket-subject"
                  required
                  value={subject()}
                  onInput={(e) => setSubject(e.currentTarget.value)}
                  placeholder={t(msg`Briefly, what's going on?`)}
                />
              </div>
              <div class="space-y-2">
                <Label>{t(msg`Category`)}</Label>
                <div class="flex flex-wrap gap-2">
                  <For each={CATEGORIES}>
                    {(c) => (
                      <button
                        type="button"
                        onClick={() => setCategory(c)}
                        aria-pressed={category() === c}
                        class="rounded-full px-4 py-1.5 text-sm font-medium ring-1 transition-colors"
                        classList={{
                          "bg-accent text-accent-foreground ring-accent": category() === c,
                          "bg-card text-muted-foreground ring-border hover:text-foreground":
                            category() !== c,
                        }}
                      >
                        {categoryLabel(c)}
                      </button>
                    )}
                  </For>
                </div>
              </div>
              <div class="space-y-2">
                <Label for="ticket-body">{t(msg`How can we help?`)}</Label>
                <textarea
                  id="ticket-body"
                  required
                  class={TEXTAREA_CLASS}
                  value={body()}
                  onInput={(e) => setBody(e.currentTarget.value)}
                  placeholder={t(msg`Tell us what happened…`)}
                />
              </div>
              <Button
                type="submit"
                loading={createMutation.isPending}
                disabled={!subject().trim() || !body().trim()}
              >
                <Send class="mr-1 h-4 w-4" />
                {t(msg`Send ticket`)}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* My tickets */}
        <div class="mt-6 space-y-3">
          <h2 class="text-lg font-semibold text-foreground">{t(msg`My tickets`)}</h2>
          <Show when={!myTickets.isLoading} fallback={<Skeleton class="h-20 w-full rounded-xl" />}>
            <Show
              when={(myTickets.data?.data.length ?? 0) > 0}
              fallback={
                <p class="text-sm text-muted-foreground">{t(msg`You have no tickets yet.`)}</p>
              }
            >
              <For each={myTickets.data?.data ?? []}>
                {(ticket) => (
                  <Card>
                    <CardContent class="space-y-2 p-4">
                      <div class="flex items-center justify-between gap-2">
                        <span class="font-medium text-foreground">{ticket.subject}</span>
                        <span
                          class="rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1"
                          classList={{
                            "bg-warning/15 text-warning ring-warning/20": ticket.status === "open",
                            "bg-accent/15 text-accent ring-accent/20":
                              ticket.status === "in_progress",
                            "bg-muted text-muted-foreground ring-border":
                              ticket.status === "closed",
                          }}
                        >
                          {ticket.status === "in_progress"
                            ? t(msg`In progress`)
                            : ticket.status === "closed"
                              ? t(msg`Closed`)
                              : t(msg`Open`)}
                        </span>
                      </div>
                      <Show when={ticket.reply}>
                        <p class="rounded-lg bg-accent/5 p-2.5 text-sm text-foreground ring-1 ring-accent/10">
                          <span class="font-medium text-accent">{t(msg`Reply`)}: </span>
                          {ticket.reply}
                        </p>
                      </Show>
                    </CardContent>
                  </Card>
                )}
              </For>
            </Show>
          </Show>
        </div>
      </Show>

      {/* Privacy & account */}
      <section class="space-y-3">
        <h2 class="text-lg font-semibold text-foreground">{t(msg`Privacy & account`)}</h2>
        <Card>
          <CardContent class="divide-y divide-border p-0">
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Data export request")}`}
              class="flex items-center justify-between gap-4 p-5 transition-colors hover:bg-muted/40"
            >
              <div class="flex items-center gap-4">
                <span class="grid h-11 w-11 place-items-center rounded-full bg-muted text-muted-foreground">
                  <Download class="h-5 w-5" />
                </span>
                <div>
                  <p class="font-medium text-foreground">{t(msg`Request my data`)}</p>
                  <p class="text-sm text-muted-foreground">
                    {t(msg`Get a copy of your collection history.`)}
                  </p>
                </div>
              </div>
              <ChevronRight class="h-5 w-5 text-muted-foreground" />
            </a>
            <Link
              to="/profile/delete-account"
              class="flex items-center justify-between gap-4 p-5 transition-colors hover:bg-destructive/5"
            >
              <div class="flex items-center gap-4">
                <span class="grid h-11 w-11 place-items-center rounded-full bg-destructive/10 text-destructive">
                  <ShieldAlert class="h-5 w-5" />
                </span>
                <div>
                  <p class="font-medium text-destructive">{t(msg`Delete my account`)}</p>
                  <p class="text-sm text-muted-foreground">
                    {t(msg`Permanent — erases your progress.`)}
                  </p>
                </div>
              </div>
              <ChevronRight class="h-5 w-5 text-muted-foreground" />
            </Link>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
